import { useState, useMemo, type ReactNode } from 'react'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { Button } from './Button'
import { MobileFullScreenPanel } from './MobileFullScreenPanel'
import { useNls } from '../../nls/nlsProvider'

export interface TabBarEditorLockedItem<K extends string = string> {
  key: K
  reason: string
}

export interface TabBarEditorTabItem<K extends string = string> {
  key: K
  label: string
  icon?: ReactNode
}

export interface TabBarEditorProps<K extends string = string> {
  isOpen: boolean
  onClose: () => void
  pinned: K[]
  more: K[]
  locked?: TabBarEditorLockedItem<K>[]
  labels?: Record<K, string> | ((key: K) => string)
  icons?: Record<K, ReactNode> | ((key: K) => ReactNode)
  tabInfo?: Record<K, TabBarEditorTabItem<K>> | ((key: K) => TabBarEditorTabItem<K>)
  onSave: (next: K[]) => void
  onResetToDefault?: () => void
  title?: string
  subtitle?: string
  resetLabel?: string
}

/**
 * Mobile full-screen editor for customizing bottom tab bar layout (TB2, TB-P9).
 * Pure UI component supporting button-driven reordering, pinning, unpinning,
 * locked state indications, and workspace default reset without Firestore or engine imports.
 */
export const TabBarEditor = <K extends string = string>({
  isOpen,
  onClose,
  pinned,
  more,
  locked = [],
  labels,
  icons,
  tabInfo,
  onSave,
  onResetToDefault,
  title,
  subtitle,
  resetLabel,
}: TabBarEditorProps<K>) => {
  const { t } = useNls()

  const [prevSync, setPrevSync] = useState({ isOpen, pinned, more })
  const [draftPinned, setDraftPinned] = useState<K[]>(pinned)
  const [draftMore, setDraftMore] = useState<K[]>(more)

  if (prevSync.isOpen !== isOpen || prevSync.pinned !== pinned || prevSync.more !== more) {
    setPrevSync({ isOpen, pinned, more })
    setDraftPinned(pinned)
    setDraftMore(more)
  }

  const lockedMap = useMemo(() => {
    const map = new Map<K, string>()
    for (const item of locked) {
      map.set(item.key, item.reason)
    }
    return map
  }, [locked])

  if (!isOpen) return null

  const resolveTab = (key: K): { label: string; icon?: ReactNode } => {
    if (typeof tabInfo === 'function') {
      const res = tabInfo(key)
      if (res) return res
    } else if (tabInfo && tabInfo[key]) {
      return tabInfo[key]
    }
    const label =
      typeof labels === 'function' ? labels(key) : (labels?.[key] ?? key)
    const icon =
      typeof icons === 'function' ? icons(key) : icons?.[key]
    return { label, icon }
  }

  const moveUp = (index: number) => {
    if (index <= 1) return // index 0 is fixed first, index 1 cannot move above it
    setDraftPinned((prev) => {
      const next = [...prev]
      const temp = next[index]
      next[index] = next[index - 1]
      next[index - 1] = temp
      return next
    })
  }

  const moveDown = (index: number) => {
    if (index <= 0 || index >= draftPinned.length - 1) return
    setDraftPinned((prev) => {
      const next = [...prev]
      const temp = next[index]
      next[index] = next[index + 1]
      next[index + 1] = temp
      return next
    })
  }

  const unpin = (key: K) => {
    if (draftPinned[0] === key) return // Fixed first cannot be unpinned
    setDraftPinned((prev) => prev.filter((k) => k !== key))
    setDraftMore((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }

  const pin = (key: K) => {
    if (draftPinned.length >= 4) return
    setDraftMore((prev) => prev.filter((k) => k !== key))
    setDraftPinned((prev) => (prev.includes(key) ? prev : [...prev, key]))
  }

  const handleReset = () => {
    onResetToDefault?.()
  }

  const handleSave = () => {
    onSave(draftPinned)
    onClose()
  }

  const isBarFull = draftPinned.length >= 4

  return (
    <MobileFullScreenPanel
      title={title ?? t('NLSCommonTabBarEditorTitle')}
      onClose={onClose}
      closeLabel={t('NLSCommonActionClose')}
      className="tabBarEditor"
    >
      <div className="flex flex-col gap-4 max-w-lg mx-auto">
        <div className="flex items-center justify-between pb-3 border-b border-[var(--border)]">
          <p className="text-xs text-[var(--muted)]">
            {subtitle ?? t('NLSCommonTabBarEditorSubtitle')}
          </p>
          {onResetToDefault ? (
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleReset}
              className="text-xs shrink-0"
              data-testid="tab-bar-editor-reset-btn"
            >
              {resetLabel ?? t('NLSCommonTabBarResetDefault')}
            </Button>
          ) : null}
        </div>

        {/* Section 1: In the bar · n of 4 */}
        <section aria-labelledby="heading-in-bar">
          <div className="flex items-center justify-between mb-2">
            <h3
              id="heading-in-bar"
              className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]"
            >
              {t('NLSCommonTabBarInBarSection', { count: draftPinned.length })}
            </h3>
          </div>

          <div
            className="flex flex-col divide-y divide-[var(--border)] border border-[var(--border)] rounded-xl bg-[var(--surface-card)] overflow-hidden"
            data-testid="editor-pinned-list"
          >
            {draftPinned.map((key, index) => {
              const info = resolveTab(key)
              const isFixedFirst = index === 0

              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
                  data-testid={`editor-pinned-row-${key}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="text-[var(--text-strong)] inline-flex items-center justify-center w-6 h-6 shrink-0"
                      aria-hidden="true"
                    >
                      {info.icon}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-[var(--text-strong)] truncate">
                        {info.label}
                      </span>
                      {isFixedFirst ? (
                        <span className="text-[10px] text-[var(--muted)] font-mono flex items-center gap-1">
                          <LockRoundedIcon fontSize="inherit" aria-hidden="true" />
                          {t('NLSCommonTabBarAlwaysFirst')}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {isFixedFirst ? (
                      <span
                        className="p-1 text-[var(--muted)]"
                        role="img"
                        aria-label={t('NLSCommonTabBarAlwaysFirst')}
                      >
                        <LockRoundedIcon fontSize="small" style={{ fontSize: '18px' }} />
                      </span>
                    ) : (
                      <>
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={index <= 1}
                          onClick={() => moveUp(index)}
                          aria-label={t('NLSCommonTabBarMoveUp')}
                          icon={<KeyboardArrowUpRoundedIcon fontSize="small" />}
                          data-testid={`editor-move-up-${key}`}
                        />
                        <Button
                          variant="tertiary"
                          size="sm"
                          disabled={index >= draftPinned.length - 1}
                          onClick={() => moveDown(index)}
                          aria-label={t('NLSCommonTabBarMoveDown')}
                          icon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
                          data-testid={`editor-move-down-${key}`}
                        />
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => unpin(key)}
                          aria-label={t('NLSCommonTabBarMoveToMore')}
                          icon={<RemoveRoundedIcon fontSize="small" />}
                          data-testid={`editor-unpin-${key}`}
                        />
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Section 2: In More */}
        <section aria-labelledby="heading-in-more">
          <div className="flex items-center justify-between mb-2">
            <h3
              id="heading-in-more"
              className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]"
            >
              {t('NLSCommonTabBarInMoreSection')}
            </h3>
          </div>

          <div
            className="flex flex-col divide-y divide-[var(--border)] border border-[var(--border)] rounded-xl bg-[var(--surface-card)] overflow-hidden"
            data-testid="editor-more-list"
          >
            {draftMore.map((key) => {
              const info = resolveTab(key)
              const lockedReason = lockedMap.get(key)
              const isLocked = Boolean(lockedReason)

              return (
                <div
                  key={key}
                  className={`flex items-center justify-between gap-3 px-3 py-2.5 text-sm ${
                    isLocked ? 'opacity-50 bg-[var(--surface-raised)] select-none' : ''
                  }`}
                  data-testid={`editor-more-row-${key}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="text-[var(--text-strong)] inline-flex items-center justify-center w-6 h-6 shrink-0"
                      aria-hidden="true"
                    >
                      {isLocked ? (
                        <LockRoundedIcon fontSize="small" style={{ fontSize: '18px' }} />
                      ) : (
                        info.icon
                      )}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-[var(--text-strong)] truncate">
                        {info.label}
                      </span>
                      {isLocked && lockedReason ? (
                        <span className="text-[10px] text-[var(--muted)] font-mono truncate">
                          {lockedReason}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {isLocked ? (
                      <span className="p-1 text-[var(--muted)]" title={lockedReason}>
                        <LockRoundedIcon fontSize="small" style={{ fontSize: '18px' }} />
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={isBarFull}
                        onClick={() => pin(key)}
                        aria-label={t('NLSCommonTabBarAddToBar')}
                        icon={<AddRoundedIcon fontSize="small" />}
                        data-testid={`editor-pin-${key}`}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Footer controls: Cancel & Save */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            data-testid="tab-bar-editor-cancel-btn"
          >
            {t('NLSCommonActionCancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            data-testid="tab-bar-editor-save-btn"
          >
            {t('NLSCommonTabBarSave')}
          </Button>
        </div>
      </div>
    </MobileFullScreenPanel>
  )
}
